import { fmtDate, todayIso, nowHM } from "@/lib/dateFormat";
import { UkDatePicker } from "@/components/UkDatePicker";
import { PaymentLog } from "@/components/PaymentLog";
import { useEffect, useMemo, useState } from "react";
import { Plus, Loader2, Building2, Trash2, Phone, Mail, Receipt, Pencil, Search, Wallet } from "lucide-react";
import { api, money, createSupplierWithDupCheck } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Loading, EmptyState } from "@/components/States";
import { UpgradePrompt } from "@/components/UpgradePrompt";
import { StatusChip } from "@/components/StatusChip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PurchaseModal } from "@/components/PurchaseModal";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const BLANK = { name: "", contact: "", phone: "", email: "" };

export default function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gate, setGate] = useState(null);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [purFor, setPurFor] = useState(null);
  const [editPur, setEditPur] = useState(null);
  const [q, setQ] = useState("");
  const [oweOnly, setOweOnly] = useState(false);
  const [payFor, setPayFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const s = await api("/suppliers");
      setSuppliers(s); setGate(null);
      setPurchases(await api("/purchases").catch(() => []));
    } catch (e) { if (e.status === 402) setGate(e.body?.detail || {}); else toast.error(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const totalOwed = useMemo(() => suppliers.reduce((s, x) => s + (x.outstanding || 0), 0), [suppliers]);

  const visible = useMemo(() => {
    const t = q.trim().toLowerCase();
    let list = suppliers.filter((s) => !t || [s.name, s.contact, s.phone, s.email].filter(Boolean).some((f) => f.toLowerCase().includes(t)));
    if (oweOnly) list = list.filter((s) => (s.outstanding || 0) > 0);
    return list;
  }, [suppliers, q, oweOnly]);

  const openNew = () => { setEditId(null); setForm(BLANK); setOpen(true); };
  const openEdit = (s) => { setEditId(s.id); setForm({ name: s.name || "", contact: s.contact || "", phone: s.phone || "", email: s.email || "" }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      if (editId) {
        await api(`/suppliers/${editId}`, { method: "PATCH", body: JSON.stringify({ name: form.name.trim(), contact: form.contact || null, phone: form.phone || null, email: form.email || null }) });
        toast.success("Supplier updated");
      } else {
        const created = await createSupplierWithDupCheck(form.name.trim(), { contact: form.contact || null, phone: form.phone || null, email: form.email || null });
        if (!created) { setSaving(false); return; }
        toast.success("Supplier added");
      }
      setOpen(false); setForm(BLANK); load();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };
  const remove = async (id) => { if (!window.confirm("Delete supplier?")) return; try { await api(`/suppliers/${id}`, { method: "DELETE" }); load(); } catch (e) { toast.error(e.message); } };

  if (loading) return <Loading />;
  if (gate) return <><PageHeader title="Suppliers & Purchasing" subtitle="Your parts & trade suppliers" /><UpgradePrompt title="Suppliers & Purchasing is a Pro feature" message={gate.message} minTier={gate.min_tier || "pro"} testid="suppliers-upgrade" /></>;
  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title="Suppliers" subtitle="Suppliers, purchases & outstanding balances"
        actions={<div className="flex gap-2">
          <Link to="/money?tab=expenses" data-testid="suppliers-expenses-link" className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2.5 text-sm font-bold text-brand-2 hover:bg-secondary"><Receipt className="h-4 w-4" />Expenses</Link>
          <button onClick={openNew} data-testid="add-supplier-btn" className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2.5 text-sm font-bold text-white"><Plus className="h-4 w-4" />Add Supplier</button>
        </div>} />

      {/* Total owed banner */}
      <div className={`mb-4 flex items-center justify-between rounded-xl border p-4 ${totalOwed > 0 ? "border-warning/40 bg-warning-bg" : "border-success/30 bg-success/10"}`} data-testid="owed-banner">
        <div className="flex items-center gap-3">
          <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${totalOwed > 0 ? "bg-warning/20 text-warning" : "bg-success/20 text-success"}`}><Wallet className="h-5 w-5" /></div>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Total outstanding</p>
            <p className={`font-display text-2xl font-bold ${totalOwed > 0 ? "text-warning" : "text-success"}`} data-testid="total-owed">{money(totalOwed)}</p>
          </div>
        </div>
        {totalOwed > 0 && <button onClick={() => setOweOnly((v) => !v)} data-testid="toggle-owe-only" className={`rounded-lg border px-3 py-2 text-xs font-bold ${oweOnly ? "border-warning bg-warning text-white" : "border-border bg-card text-brand-2"}`}>{oweOnly ? "Showing who you owe" : "Show who you owe"}</button>}
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search supplier name, contact, phone…" data-testid="supplier-search"
          className="w-full rounded-lg border border-input bg-card py-2.5 pl-10 pr-3 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" />
      </div>

      {visible.length === 0 ? <EmptyState icon={Building2} title={q || oweOnly ? "No matching suppliers" : "No suppliers yet"} /> : (
        <div className="space-y-3">
          {visible.map((s) => {
            const sPur = purchases.filter((p) => p.supplier_id === s.id);
            return (
              <div key={s.id} data-testid={`supplier-${s.id}`} className="rounded-xl border border-border bg-card p-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-tertiary text-brand"><Building2 className="h-5 w-5" /></div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-bold text-midnight">{s.name}</p>
                    {s.contact && <p className="text-xs text-muted-foreground">{s.contact}</p>}
                    {s.phone && <p className="flex items-center gap-1 text-xs text-muted-foreground"><Phone className="h-3 w-3" />{s.phone}</p>}
                    {s.email && <p className="flex items-center gap-1 truncate text-xs text-muted-foreground"><Mail className="h-3 w-3" />{s.email}</p>}
                  </div>
                  <div className="flex flex-col items-end gap-1.5">
                    {s.outstanding > 0 ? (
                      <span data-testid={`supplier-balance-${s.id}`} className="inline-block rounded-full bg-warning-bg px-2.5 py-1 text-xs font-bold text-warning">Owe {money(s.outstanding)}</span>
                    ) : (
                      <span data-testid={`supplier-balance-${s.id}`} className="inline-block rounded-full bg-success/10 px-2.5 py-1 text-xs font-bold text-success">Settled</span>
                    )}
                    <div className="flex items-center gap-2">
                      <button onClick={() => openEdit(s)} data-testid={`edit-supplier-${s.id}`} className="text-brand-2"><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => remove(s.id)} data-testid={`delete-supplier-${s.id}`} className="text-danger"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                </div>

                {s.outstanding > 0 && (
                  <button onClick={() => setPayFor(s)} data-testid={`record-payment-btn-${s.id}`} className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-success/40 bg-success/10 py-2 text-sm font-bold text-success hover:bg-success/20">
                    <Wallet className="h-4 w-4" />Record a payment
                  </button>
                )}

                <div className="mt-3 flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Purchases ({sPur.length})</span>
                  <button onClick={() => setPurFor(s.id)} data-testid={`new-purchase-btn-${s.id}`} className="inline-flex items-center gap-1 text-xs font-bold text-brand-2"><Plus className="h-3.5 w-3.5" />New purchase</button>
                </div>
                {sPur.length > 0 && (
                  <div className="mt-2 divide-y divide-border rounded-lg border border-border">
                    {sPur.map((p) => (
                      <button key={p.id} onClick={() => setEditPur({ supplierId: s.id, purchase: p })} data-testid={`purchase-row-${p.id}`} className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-secondary">
                        <Receipt className="h-4 w-4 text-brand-2" />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-midnight">{p.supplier_invoice_number || `Purchase · ${fmtDate(p.date)}`}</p>
                          <p className="truncate text-[11px] text-muted-foreground">{(p.lines || []).length} line(s) · {(p.lines || []).some((l) => l.allocation === "job") ? "job order" : "stock"}</p>
                        </div>
                        <StatusChip status={p.status === "received" ? "completed" : p.status === "cancelled" ? "void" : "coming_soon"} />
                        <StatusChip status={p.payment_status === "paid" ? "paid" : p.payment_status === "partial" ? "part_paid" : "unpaid"} />
                        <span className="font-display text-sm font-bold text-midnight">{money(p.total)}</span>
                        <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent><DialogHeader><DialogTitle className="font-display uppercase">{editId ? "Edit Supplier" : "Add Supplier"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" data-testid="supplier-name" className="w-full rounded-lg border border-input px-3.5 py-2.5 text-sm outline-none focus:border-brand" />
            <input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} placeholder="Contact person" data-testid="supplier-contact" className="w-full rounded-lg border border-input px-3.5 py-2.5 text-sm outline-none focus:border-brand" />
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" data-testid="supplier-phone" className="w-full rounded-lg border border-input px-3.5 py-2.5 text-sm outline-none focus:border-brand" />
            <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" data-testid="supplier-email" className="w-full rounded-lg border border-input px-3.5 py-2.5 text-sm outline-none focus:border-brand" />
          </div>
          <DialogFooter><button onClick={save} disabled={saving} data-testid="supplier-save" className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand py-3 text-sm font-extrabold text-white disabled:opacity-60">{saving ? <Loader2 className="h-5 w-5 animate-spin" /> : editId ? "SAVE CHANGES" : "ADD SUPPLIER"}</button></DialogFooter>
        </DialogContent>
      </Dialog>

      <PaySupplierDialog supplier={payFor} onClose={() => setPayFor(null)} onPaid={() => { setPayFor(null); load(); }} />
      <PurchaseModal open={!!purFor} onClose={() => setPurFor(null)} supplierId={purFor} onSaved={load} />
      <PurchaseModal open={!!editPur} onClose={() => setEditPur(null)} supplierId={editPur?.supplierId} edit={editPur?.purchase} onSaved={load} />
    </div>
  );
}

function PaySupplierDialog({ supplier, onClose, onPaid }) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("bank_transfer");
  const [payDate, setPayDate] = useState(todayIso());
  const [payTime, setPayTime] = useState(nowHM());
  const [busy, setBusy] = useState(false);
  const [logs, setLogs] = useState(null);
  useEffect(() => {
    if (supplier) {
      setAmount(String(supplier.outstanding ?? "")); setPayDate(todayIso()); setPayTime(nowHM());
      setLogs(null);
      api(`/purchases/supplier/${supplier.id}/payments`).then(setLogs).catch(() => setLogs([]));
    }
  }, [supplier]);
  const submit = async () => {
    const amt = Number(amount);
    if (!(amt > 0)) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      const r = await api(`/purchases/supplier/${supplier.id}/pay`, { method: "POST", body: JSON.stringify({ amount: amt, method, paid_at: `${payDate}T${payTime || "00:00"}:00` }) });
      toast.success(`Payment of ${money(r.applied)} recorded${r.unapplied > 0 ? ` · ${money(r.unapplied)} unallocated` : ""}`);
      onPaid();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!supplier} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display uppercase">Record payment · {supplier?.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">Outstanding: <b className="text-warning">{money(supplier?.outstanding || 0)}</b>. Payment is applied to the oldest unpaid purchases first.</p>
          <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount £" data-testid="pay-amount" className="w-full rounded-lg border border-input px-3.5 py-2.5 text-sm outline-none focus:border-brand" />
          <select value={method} onChange={(e) => setMethod(e.target.value)} data-testid="pay-method" className="w-full rounded-lg border border-input px-3.5 py-2.5 text-sm outline-none focus:border-brand">
            <option value="bank_transfer">Bank transfer</option>
            <option value="card">Card</option>
            <option value="cash">Cash</option>
            <option value="direct_debit">Direct debit</option>
            <option value="other">Other</option>
          </select>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2"><span className="mb-1 block text-[11px] font-bold tracking-widest text-muted-foreground">PAYMENT DATE</span>
              <UkDatePicker value={payDate} onChange={setPayDate} testId="pay-date" /></div>
            <label className="block"><span className="mb-1 block text-[11px] font-bold tracking-widest text-muted-foreground">TIME</span>
              <input type="time" value={payTime} onChange={(e) => setPayTime(e.target.value)} data-testid="pay-time" className="w-full rounded-lg border border-input px-3.5 py-2.5 text-sm outline-none focus:border-brand" /></label>
          </div>
          <PaymentLog logs={logs} />
        </div>
        <DialogFooter><button onClick={submit} disabled={busy} data-testid="pay-save" className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand py-3 text-sm font-extrabold text-white disabled:opacity-60">{busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "RECORD PAYMENT"}</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
